import React, { useCallback, useContext } from 'react';

import { FileUploadContext } from './FileUploadContext';

/**
 * FileUploadTrigger component
 *
 * This component wraps a child element (typically a button) to trigger the file selection dialog.
 * It must be used within a FileUploadContextProvider component.
 *
 * @component
 * @example
 * <FileUploadTrigger>
 *   <Button>Select Files</Button>
 * </FileUploadTrigger>
 */
export function FileUploadTrigger({ children }: { children: React.ReactElement }) {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error('FileUploadTrigger must be used within a FileUploadContext.Provider');
  }

  const handleClick = useCallback(() => {
    if (context.fileInputRef.current) {
      context.fileInputRef.current.click();
    }
  }, [context.fileInputRef]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      context.handleFileInputChange(
        event,
        context.defaultAttachedToKind,
        context.defaultAttachedToId,
      );
    },
    [context],
  );

  return (
    <>
      <div onClick={handleClick}>{React.cloneElement(children)}</div>
      <input
        type="file"
        ref={context.fileInputRef}
        style={{ display: 'none' }}
        onChange={handleChange}
        accept={context.acceptExtensions}
        multiple={context.allowMultipleFiles}
      />
    </>
  );
}
